import {
    getViewerBinaryCacheKindFromType,
    normalizeViewerBinaryCacheRequest,
} from '../viewer-cache/resourceTypes';

const DEFAULT_VIEWER_BINARY_CACHE_MAX_BYTES = 256 * 1024 * 1024;
const DEFAULT_VIEWER_BINARY_CACHE_MAX_IDLE_MS = 15 * 60 * 1000;

function normalizeContentType(contentType) {
    return String(contentType || '').trim().toLowerCase();
}

function getBinaryByteSize(data) {
    if (data instanceof ArrayBuffer) {
        return data.byteLength;
    }

    if (ArrayBuffer.isView(data)) {
        return data.byteLength;
    }

    if (data instanceof Blob) {
        return data.size;
    }

    return 0;
}

function buildCacheKey(kind, url) {
    return `${kind}:${url}`;
}

function buildCacheKeyFromType(type, url) {
    return buildCacheKey(getViewerBinaryCacheKindFromType(type), url);
}

function createAbortError() {
    const error = new Error('The operation was aborted.');
    error.name = 'AbortError';
    return error;
}

export function isAbortError(error) {
    return error?.name === 'AbortError'
        || error?.name === 'CanceledError'
        || error?.code === 'ERR_CANCELED'
        || error?.message === 'canceled';
}

function createReadyEntry({
    cacheKey,
    contentType,
    data,
    leases = new Map(),
    url,
}) {
    return {
        byteSize: getBinaryByteSize(data),
        cacheKey,
        contentType: normalizeContentType(contentType),
        data,
        idleTimerId: null,
        lastUsedAt: Date.now(),
        leases: new Map(leases),
        url,
    };
}

function getLeaseScopeId(scopeId) {
    return scopeId == null ? null : String(scopeId);
}

function createLease(scopeId) {
    return {
        scopeId: getLeaseScopeId(scopeId),
    };
}

function createPublicReadyResource(readyEntry) {
    return {
        byteSize: readyEntry.byteSize,
        cacheKey: readyEntry.cacheKey,
        contentType: readyEntry.contentType,
        data: readyEntry.data,
        url: readyEntry.url,
    };
}

export function createBinaryBlob(resource, fallbackType = 'application/octet-stream') {
    return new Blob(
        [resource.data],
        {
            type: resource.contentType || fallbackType,
        },
    );
}

export function createObjectUrlFromBinaryResource(resource, fallbackType = 'application/octet-stream') {
    return URL.createObjectURL(createBinaryBlob(resource, fallbackType));
}

export function createViewerBinaryCache({
    fetchBinary,
    maxBytes = DEFAULT_VIEWER_BINARY_CACHE_MAX_BYTES,
    maxIdleMs = DEFAULT_VIEWER_BINARY_CACHE_MAX_IDLE_MS,
}) {
    if (typeof fetchBinary !== 'function') {
        throw new Error('viewerBinaryCache requires a fetchBinary function.');
    }

    const readyEntries = new Map();
    const inflightEntries = new Map();
    let totalReadyBytes = 0;
    const normalizedMaxIdleMs = Number.isFinite(maxIdleMs)
        ? Math.max(0, maxIdleMs)
        : Infinity;

    const isReadyEntryConsumed = (readyEntry) => readyEntry.leases.size > 0;

    const clearReadyEntryIdleTimer = (readyEntry) => {
        if (readyEntry?.idleTimerId === null) {
            return;
        }

        const targetReadyEntry = readyEntry;
        clearTimeout(targetReadyEntry.idleTimerId);
        targetReadyEntry.idleTimerId = null;
    };

    const isReadyEntryStale = (readyEntry) => (
        normalizedMaxIdleMs !== Infinity
        && Date.now() - readyEntry.lastUsedAt >= normalizedMaxIdleMs
    );

    const removeReadyEntryByCacheKey = (cacheKey) => {
        const readyEntry = readyEntries.get(cacheKey);

        if (!readyEntry) {
            return false;
        }

        clearReadyEntryIdleTimer(readyEntry);
        readyEntries.delete(cacheKey);
        totalReadyBytes = Math.max(0, totalReadyBytes - readyEntry.byteSize);

        return true;
    };

    const scheduleReadyEntryIdleTimer = (readyEntry) => {
        clearReadyEntryIdleTimer(readyEntry);

        if (
            normalizedMaxIdleMs === Infinity
            || isReadyEntryConsumed(readyEntry)
            || !readyEntries.has(readyEntry.cacheKey)
        ) {
            return;
        }

        const targetReadyEntry = readyEntry;
        const delay = Math.max(
            0,
            normalizedMaxIdleMs - (Date.now() - targetReadyEntry.lastUsedAt),
        );

        const scheduledReadyEntry = targetReadyEntry;
        scheduledReadyEntry.idleTimerId = setTimeout(() => {
            scheduledReadyEntry.idleTimerId = null;

            const activeReadyEntry = readyEntries.get(scheduledReadyEntry.cacheKey);

            if (!activeReadyEntry || activeReadyEntry !== scheduledReadyEntry) {
                return;
            }

            if (isReadyEntryConsumed(activeReadyEntry)) {
                return;
            }

            if (isReadyEntryStale(activeReadyEntry)) {
                removeReadyEntryByCacheKey(activeReadyEntry.cacheKey);
                return;
            }

            scheduleReadyEntryIdleTimer(activeReadyEntry);
        }, delay);
    };

    const markReadyEntryUsed = (readyEntry) => {
        clearReadyEntryIdleTimer(readyEntry);
        const usedReadyEntry = readyEntry;
        usedReadyEntry.lastUsedAt = Date.now();
        readyEntries.delete(usedReadyEntry.cacheKey);
        readyEntries.set(usedReadyEntry.cacheKey, usedReadyEntry);
    };

    const evictToBudget = () => {
        while (totalReadyBytes > maxBytes && readyEntries.size > 0) {
            const removableReadyEntry = Array.from(readyEntries.entries())
                .find(([, readyEntry]) => !isReadyEntryConsumed(readyEntry));

            if (!removableReadyEntry) {
                break;
            }

            removeReadyEntryByCacheKey(removableReadyEntry[0]);
        }
    };

    const invalidateByCacheKey = (cacheKey) => {
        if (!cacheKey) {
            return false;
        }

        return removeReadyEntryByCacheKey(cacheKey);
    };

    const releaseLease = (cacheKey, leaseId) => {
        if (!cacheKey || !leaseId) {
            return;
        }

        const inflightEntry = inflightEntries.get(cacheKey);

        if (inflightEntry) {
            inflightEntry.leases.delete(leaseId);

            if (inflightEntry.leases.size === 0) {
                inflightEntries.delete(cacheKey);
                inflightEntry.abortController.abort();
            }

            return;
        }

        const readyEntry = readyEntries.get(cacheKey);

        if (!readyEntry || !readyEntry.leases.delete(leaseId)) {
            return;
        }

        if (readyEntry.leases.size > 0) {
            return;
        }

        markReadyEntryUsed(readyEntry);
        scheduleReadyEntryIdleTimer(readyEntry);
        evictToBudget();
    };

    const createReleaseHandle = (cacheKey, leaseId) => {
        let didRelease = false;

        return () => {
            if (didRelease) {
                return;
            }

            didRelease = true;
            releaseLease(cacheKey, leaseId);
        };
    };

    const acquire = (request = {}) => {
        const normalizedRequest = normalizeViewerBinaryCacheRequest(request);
        const {
            accept,
            consumerId,
            scopeId,
            type,
            url,
            validateResource,
        } = normalizedRequest;

        if (!type || !url) {
            return {
                cacheKey: null,
                promise: Promise.reject(new Error('viewerBinaryCache.acquire requires both a resource type and url.')),
                release() {},
            };
        }

        const cacheKey = buildCacheKeyFromType(type, url);
        const leaseId = Symbol(consumerId || cacheKey);
        const lease = createLease(scopeId);
        const release = createReleaseHandle(cacheKey, leaseId);
        let readyEntry = readyEntries.get(cacheKey);

        if (readyEntry && !isReadyEntryConsumed(readyEntry) && isReadyEntryStale(readyEntry)) {
            removeReadyEntryByCacheKey(cacheKey);
            readyEntry = null;
        }

        if (readyEntry) {
            markReadyEntryUsed(readyEntry);
            readyEntry.leases.set(leaseId, lease);

            return {
                cacheKey,
                promise: Promise.resolve(createPublicReadyResource(readyEntry)),
                release,
            };
        }

        const inflightEntry = inflightEntries.get(cacheKey);

        if (inflightEntry) {
            inflightEntry.leases.set(leaseId, lease);

            return {
                cacheKey,
                promise: inflightEntry.promise,
                release,
            };
        }

        const abortController = new AbortController();
        const nextInflightEntry = {
            abortController,
            leases: new Map([[leaseId, lease]]),
            promise: null,
        };

        nextInflightEntry.promise = Promise.resolve()
            .then(() => fetchBinary(url, accept, { signal: abortController.signal }))
            .then((response) => {
                if (inflightEntries.get(cacheKey) !== nextInflightEntry) {
                    throw createAbortError();
                }

                let readyResource = {
                    byteSize: getBinaryByteSize(response?.data),
                    cacheKey,
                    contentType: normalizeContentType(response?.headers?.['content-type']),
                    data: response?.data,
                    url,
                };

                if (typeof validateResource === 'function') {
                    const validatedPatch = validateResource(readyResource, response);

                    if (validatedPatch && typeof validatedPatch === 'object') {
                        readyResource = {
                            ...readyResource,
                            ...validatedPatch,
                        };
                        readyResource.byteSize = getBinaryByteSize(readyResource.data);
                        readyResource.contentType = normalizeContentType(readyResource.contentType);
                    }
                }

                inflightEntries.delete(cacheKey);
                readyEntry = createReadyEntry({
                    ...readyResource,
                    leases: nextInflightEntry.leases,
                });
                readyEntries.set(cacheKey, readyEntry);
                totalReadyBytes += readyEntry.byteSize;
                markReadyEntryUsed(readyEntry);

                if (!isReadyEntryConsumed(readyEntry)) {
                    scheduleReadyEntryIdleTimer(readyEntry);
                }

                evictToBudget();

                return createPublicReadyResource(readyEntry);
            })
            .catch((error) => {
                if (inflightEntries.get(cacheKey) === nextInflightEntry) {
                    inflightEntries.delete(cacheKey);
                }

                throw error;
            });

        inflightEntries.set(cacheKey, nextInflightEntry);

        return {
            cacheKey,
            promise: nextInflightEntry.promise,
            release,
        };
    };

    return {
        acquire,
        abortInflight({ scopeId } = {}) {
            const targetScopeId = getLeaseScopeId(scopeId);

            if (!targetScopeId) {
                return 0;
            }

            let abortedCount = 0;

            inflightEntries.forEach((inflightEntry, cacheKey) => {
                inflightEntry.leases.forEach((lease, leaseId) => {
                    if (lease.scopeId !== targetScopeId) {
                        return;
                    }

                    inflightEntry.leases.delete(leaseId);
                });

                if (inflightEntry.leases.size > 0) {
                    return;
                }

                inflightEntries.delete(cacheKey);
                inflightEntry.abortController.abort();
                abortedCount += 1;
            });

            return abortedCount;
        },
        clear() {
            inflightEntries.forEach((inflightEntry) => {
                inflightEntry.abortController.abort();
            });
            inflightEntries.clear();
            readyEntries.forEach((readyEntry) => {
                clearReadyEntryIdleTimer(readyEntry);
            });
            readyEntries.clear();
            totalReadyBytes = 0;
        },
        invalidate({ cacheKey, type, url } = {}) {
            const targetCacheKey = cacheKey || (type && url ? buildCacheKeyFromType(type, url) : null);

            return invalidateByCacheKey(targetCacheKey);
        },
        getSnapshot() {
            return {
                consumedReadyKeys: Array.from(readyEntries.values())
                    .filter((readyEntry) => isReadyEntryConsumed(readyEntry))
                    .map((readyEntry) => readyEntry.cacheKey),
                inflightKeys: Array.from(inflightEntries.keys()),
                maxIdleMs: normalizedMaxIdleMs,
                maxBytes,
                readyKeys: Array.from(readyEntries.keys()),
                totalReadyBytes,
            };
        },
        isAbortError,
    };
}

export {
    DEFAULT_VIEWER_BINARY_CACHE_MAX_IDLE_MS,
    DEFAULT_VIEWER_BINARY_CACHE_MAX_BYTES,
    buildCacheKey as buildViewerBinaryCacheKey,
    buildCacheKeyFromType as buildViewerBinaryCacheKeyFromType,
    createAbortError,
    normalizeContentType,
};
